import { useState, useEffect } from "react";
import { format, parse, isValid } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// Auto-format raw keystrokes into dd/MM/yyyy: strips non-digits, inserts the
// "/" separators automatically after day and month, and lightly clamps an
// obviously-invalid completed day (>31) or month (>12). e.g. "25091992" -> "25/09/1992".
function maskDate(raw) {
  const s = String(raw || "").replace(/\D/g, "").slice(0, 8);
  let dd = s.slice(0, 2);
  let mm = s.slice(2, 4);
  const yy = s.slice(4, 8);
  if (dd.length === 2) {
    const n = parseInt(dd, 10);
    if (n < 1) dd = "01";
    else if (n > 31) dd = "31";
  }
  if (mm.length === 2) {
    const n = parseInt(mm, 10);
    if (n < 1) mm = "01";
    else if (n > 12) mm = "12";
  }
  let out = dd;
  if (s.length > 2) out += "/" + mm;
  if (s.length > 4) out += "/" + yy;
  return out;
}

// User-facing date field: DISPLAYS dd/MM/yyyy but keeps value in the DB's
// ISO yyyy-MM-dd format. This is locale-independent (unlike native <input
// type="date">) and never swaps day/month internally.
export function DateInput({ value, onChange, id, className, placeholder = "DD/MM/YYYY", disabled, "data-testid": testId }) {
  const isoToText = (iso) => {
    if (!iso) return "";
    const d = parse(String(iso).slice(0, 10), "yyyy-MM-dd", new Date());
    return isValid(d) ? format(d, "dd/MM/yyyy") : "";
  };
  const [text, setText] = useState(isoToText(value));
  const [open, setOpen] = useState(false);

  useEffect(() => { setText(isoToText(value)); }, [value]);

  const commit = (raw) => {
    const cleaned = (raw || "").trim();
    if (!cleaned) { onChange(""); return; }
    const d = parse(cleaned, "dd/MM/yyyy", new Date());
    // strict: reject impossible dates (date-fns would otherwise roll them over)
    if (isValid(d) && format(d, "dd/MM/yyyy") === cleaned) {
      onChange(format(d, "yyyy-MM-dd"));
    } else {
      setText(isoToText(value)); // revert invalid input to last good value
    }
  };

  const selected = value ? parse(String(value).slice(0, 10), "yyyy-MM-dd", new Date()) : undefined;
  const validSelected = selected && isValid(selected) ? selected : undefined;

  return (
    <div className={`relative ${className || ""}`}>
      <Input
        id={id}
        data-testid={testId}
        value={text}
        placeholder={placeholder}
        inputMode="numeric"
        disabled={disabled}
        autoComplete="off"
        onChange={(e) => setText(maskDate(e.target.value))}
        onBlur={(e) => commit(e.target.value)}
        className="pr-10"
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={disabled}
            aria-label="Open date picker"
            data-testid={testId ? `${testId}-picker` : undefined}
            className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 text-muted-foreground hover:text-navy"
          >
            <CalendarIcon className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={validSelected}
            defaultMonth={validSelected}
            captionLayout="dropdown-buttons"
            fromYear={1900}
            toYear={new Date().getFullYear()}
            onSelect={(d) => {
              if (d) {
                onChange(format(d, "yyyy-MM-dd"));
                setText(format(d, "dd/MM/yyyy"));
              }
              setOpen(false);
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
